import { useSearchParams } from "@/lib/router-compat";
import { CheckCircle2, Building2 } from "lucide-react";
import { BcbsilOptinForm } from "./BcbsilOptinForm";
import { getEmployerNameFromParams } from "./employerParam";

interface BcbsilDetailSectionsProps {
  mriImg: string;
  brainHeroImg: string;
  chairImg: string;
}

export function BcbsilDetailSections({
  mriImg,
  brainHeroImg,
  chairImg,
}: BcbsilDetailSectionsProps) {
  const [searchParams] = useSearchParams();
  const employer = getEmployerNameFromParams(searchParams);
  return (
    <>
      {/* SECTION: What SAINT is */}
      <section id="saint-how" className="py-14 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-left">
          <div className="max-w-3xl space-y-3 mb-12">
            <span className="inline-block text-xs uppercase tracking-wider font-semibold text-accent">
              What SAINT is
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-primary tracking-tight">
              Mapping your brain, and your way forward
            </h2>
            <p className="text-base sm:text-lg text-foreground leading-relaxed">
              SAINT (Stanford Accelerated Intelligent Neuromodulation Therapy)
              is a personalized form of TMS developed at Stanford. Instead of
              aiming at a standard spot for everyone, it uses an MRI of your
              brain to find your own target, then treats it with gentle magnetic
              pulses.{" "}
              <a
                href="/saint"
                className="underline font-medium text-primary hover:text-accent"
              >
                Read our full SAINT® TMS overview
              </a>
              .
            </p>
          </div>

          {/* Three step cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {/* Step 1 */}
            <div className="rounded-2xl border border-border overflow-hidden bg-white shadow-xs flex flex-col">
              <div className="aspect-16/10 overflow-hidden bg-muted">
                <img
                  src={mriImg}
                  alt="45-minute brain scan for SAINT personalized mapping"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 flex-1 flex flex-col justify-between">
                <div>
                  <span className="text-xs uppercase tracking-wider font-bold text-accent block mb-1">
                    Step 1
                  </span>
                  <h3 className="text-lg sm:text-xl font-semibold text-primary mb-2">
                    A 45-minute brain scan.
                  </h3>
                  <p className="text-sm text-foreground leading-relaxed">
                    A painless MRI creates a map of your brain&apos;s
                    connections. We coordinate the scan for you.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="rounded-2xl border border-border overflow-hidden bg-white shadow-xs flex flex-col">
              <div className="aspect-16/10 overflow-hidden bg-muted">
                <img
                  src={brainHeroImg}
                  alt="Personalized brain target identified by SAINT software"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 flex-1 flex flex-col justify-between">
                <div>
                  <span className="text-xs uppercase tracking-wider font-bold text-accent block mb-1">
                    Step 2
                  </span>
                  <h3 className="text-lg sm:text-xl font-semibold text-primary mb-2">
                    Your individual target.
                  </h3>
                  <p className="text-sm text-foreground leading-relaxed">
                    SAINT&apos;s software finds the precise spot in your brain
                    linked to depression, so treatment is aimed at you.
                  </p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="rounded-2xl border border-border overflow-hidden bg-white shadow-xs flex flex-col">
              <div className="aspect-16/10 overflow-hidden bg-muted">
                <img
                  src={chairImg}
                  alt="Private outpatient treatment suite for five-day SAINT protocol"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 flex-1 flex flex-col justify-between">
                <div>
                  <span className="text-xs uppercase tracking-wider font-bold text-accent block mb-1">
                    Step 3
                  </span>
                  <h3 className="text-lg sm:text-xl font-semibold text-primary mb-2">
                    Five days of treatment.
                  </h3>
                  <p className="text-sm text-foreground leading-relaxed">
                    Ten 10-minute sessions a day with 50-minute breaks, Monday
                    through Friday. You&apos;re awake, can read or work between
                    sessions, and can drive yourself.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION: Coverage */}
      <section
        id="saint-coverage"
        className="py-14 sm:py-20 bg-muted border-y border-border"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-left">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            {/* Left Column */}
            <div className="lg:col-span-7 space-y-6">
              <div>
                <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-accent mb-1">
                  {employer ? (
                    <>
                      <Building2 className="w-3.5 h-3.5 text-accent shrink-0" />
                      <span>{employer} · BCBSIL Coverage</span>
                    </>
                  ) : (
                    "Your Blue Cross and Blue Shield of Illinois plan"
                  )}
                </span>
                <h2 className="text-2xl sm:text-3xl font-semibold text-primary tracking-tight">
                  We&apos;ll do the insurance legwork for you
                </h2>
              </div>

              <p className="text-base text-foreground leading-relaxed">
                {employer ? (
                  <>
                    SAINT may be covered under your{" "}
                    <strong className="font-semibold text-primary">
                      {employer}
                    </strong>{" "}
                    Blue Cross and Blue Shield of Illinois plan, for you and for
                    eligible family members on your policy. Every employer group
                    plan has unique terms, so we check yours before anything is
                    scheduled.
                  </>
                ) : (
                  "SAINT may be covered under your BCBSIL plan, for you and for eligible family members on your plan. Every plan is different, so we check yours before anything is scheduled."
                )}
              </p>

              {/* Numbered Steps */}
              <div className="space-y-4 pt-2">
                <div className="flex items-start gap-4 p-4 rounded-xl bg-white border border-border">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm shrink-0 mt-0.5">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary text-base mb-1">
                      Tell us about your plan.
                    </h3>
                    <p className="text-sm text-foreground leading-relaxed">
                      Share a few details and a copy of your member card.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-white border border-border">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm shrink-0 mt-0.5">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary text-base mb-1">
                      We verify benefits and request approval.
                    </h3>
                    <p className="text-sm text-foreground leading-relaxed">
                      Our team handles the benefits check, prior authorization,
                      and documentation.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-white border border-border">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm shrink-0 mt-0.5">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary text-base mb-1">
                      You know your cost first.
                    </h3>
                    <p className="text-sm text-foreground leading-relaxed">
                      You&apos;ll see what you&apos;d pay before you commit to
                      anything.
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground leading-relaxed pt-2">
                Coverage varies by plan and isn&apos;t guaranteed. Insurance
                approval takes longer than self-pay, which is also available.
                For benefits teams: SAINT is billed with CPT codes 0889T–0892T.{" "}
                <a
                  href="/cost-insurance"
                  className="underline text-primary hover:text-accent"
                >
                  Learn more about cost and insurance details
                </a>
                .
              </p>
            </div>

            {/* Right Column — Callout Box */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl p-6 sm:p-8 bg-white border border-border shadow-sm space-y-4">
                <span className="text-xs uppercase tracking-wider font-semibold text-accent block">
                  Location & Travel
                </span>
                <h3 className="text-xl sm:text-2xl font-semibold text-primary">
                  Where treatment happens.
                </h3>
                <p className="text-sm sm:text-base text-foreground leading-relaxed">
                  Your consultation is over Zoom. Your scan is coordinated for
                  you, and your treatment week takes place at our clinic in
                  Coral Springs, Florida. Our travel coordinator helps you plan
                  it.
                </p>

                <div className="pt-2 border-t border-border">
                  <a
                    href="/traveling-for-treatment"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:text-accent/80 underline"
                  >
                    View travel planning and accommodations guide →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION: Testimonials */}
      <section className="py-14 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-left">
          <div className="max-w-2xl mb-12">
            <span className="inline-block text-xs uppercase tracking-wider font-semibold text-accent mb-1">
              Patient Experiences
            </span>
            <h2 className="text-2xl sm:text-3xl font-semibold text-primary tracking-tight">
              In their own words
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="rounded-2xl p-6 sm:p-8 bg-muted border border-border flex flex-col justify-between">
              <blockquote className="text-sm sm:text-base text-foreground leading-relaxed italic mb-4">
                &ldquo;After years of medications that didn&apos;t work, SAINT
                gave me my life back in one week. I felt a shift by day
                three.&rdquo;
              </blockquote>
              <div className="text-xs font-semibold text-primary">
                — Tami M., SAINT patient
              </div>
            </div>

            <div className="rounded-2xl p-6 sm:p-8 bg-muted border border-border flex flex-col justify-between">
              <blockquote className="text-sm sm:text-base text-foreground leading-relaxed italic mb-4">
                &ldquo;I was skeptical that five days could do what six weeks of
                TMS didn&apos;t. The fMRI targeting made it different, and I
                noticed the difference fast.&rdquo;
              </blockquote>
              <div className="text-xs font-semibold text-primary">
                — Jeannie R., SAINT patient
              </div>
            </div>

            <div className="rounded-2xl p-6 sm:p-8 bg-muted border border-border flex flex-col justify-between">
              <blockquote className="text-sm sm:text-base text-foreground leading-relaxed italic mb-4">
                &ldquo;Traveling from out of state felt like a lot, but the team
                coordinated everything. Five days later I felt like myself
                again.&rdquo;
              </blockquote>
              <div className="text-xs font-semibold text-primary">
                — David T., SAINT patient
              </div>
            </div>
          </div>

          <p className="mt-8 text-xs text-muted-foreground leading-relaxed text-center md:text-left">
            Fine print: Individual results vary. Patient stories reflect
            personal experiences and are not a guarantee of outcome.
          </p>
        </div>
      </section>

      {/* SECTION: Opt-in */}
      <section
        id="saint-optin"
        className="py-16 sm:py-24 bg-primary text-primary-foreground"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-left">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            {/* Left column */}
            <div className="lg:col-span-6 space-y-6">
              <span className="inline-block text-xs uppercase tracking-wider font-semibold text-accent">
                Complimentary Consultation
              </span>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white leading-tight">
                {employer
                  ? `Take the first step with your ${employer} plan.`
                  : "Take the first step. We'll handle the rest."}
              </h2>
              <p className="text-base sm:text-lg text-white/90 leading-relaxed">
                {employer
                  ? `Request a free consultation and ${employer} BCBSIL benefits check. A care coordinator will reach out within one business day.`
                  : "Request a free consultation and BCBSIL benefits check. A care coordinator will reach out within one business day."}
              </p>

              <div className="space-y-3.5 pt-2">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent shrink-0" />
                  <span className="text-sm sm:text-base text-white/95">
                    Warm, judgment-free conversation
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent shrink-0" />
                  <span className="text-sm sm:text-base text-white/95">
                    We check your plan&apos;s benefits for you
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-accent shrink-0" />
                  <span className="text-sm sm:text-base text-white/95">
                    Evening and Saturday appointments available
                  </span>
                </div>
              </div>
            </div>

            {/* Right column — Form Card */}
            <div className="lg:col-span-6">
              <div className="rounded-2xl p-6 sm:p-8 bg-white text-foreground shadow-xl border border-white/10">
                <BcbsilOptinForm employerName={employer} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
